import { el } from '../ir/constructors';
import { isString } from '../ir/guards';
import type { Child, ElementRef, ElNode, Props } from '../ir/types';
import type { AriaAttributes, RoleAttribute } from '../tsx/aria';
import { isDomNode, isInstruction, isSignalLike } from '../ir/values';

/** Event props typed by the DOM event map: `onClick`, `onInput`, … take a handler or an action key. */
export type EventProps = {
  readonly [K in keyof HTMLElementEventMap as `on${K}` | `on${Capitalize<K>}`]?:
    | ((event: HTMLElementEventMap[K]) => void)
    | string;
};

/** Author props with typed events, ARIA attributes, roles and a ref. */
export type TagProps = Props & EventProps & AriaAttributes & RoleAttribute & { readonly ref?: ElementRef };

/** One factory per HTML tag: `h.div(props, ...children)` or `h.div(...children)`. */
/** Every HTML tag, and the SVG tags HTML does not also name; the factory creates each in its own namespace. */
type TagName = keyof HTMLElementTagNameMap | Exclude<keyof SVGElementTagNameMap, keyof HTMLElementTagNameMap>;

export type H = {
  readonly [K in TagName]: {
    (props: TagProps | null, ...children: readonly Child[]): ElNode;
    (...children: readonly Child[]): ElNode;
  };
};

type Factory = (...args: readonly (TagProps | null | Child)[]) => ElNode;

function isPlainProps(value: TagProps | null | Child): value is TagProps {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !isInstruction(value) &&
    !isSignalLike(value) &&
    !isDomNode(value)
  );
}

/** Builds an element for any tag, including custom elements the tag map does not know. */
export function element(tag: string, ...args: readonly (TagProps | null | Child)[]): ElNode {
  const [first, ...rest] = args;
  if (first === null || (first !== undefined && isPlainProps(first))) {
    return el(tag, first, ...childrenOf(rest));
  }
  return el(tag, null, ...childrenOf(args));
}

/** Everything after the props position is a child; a typed handler is one at runtime. */
function childrenOf(args: readonly (TagProps | null | Child)[]): Child[] {
  const out: Child[] = [];
  for (const arg of args) {
    if (arg === null || arg === undefined) continue;
    if (isPlainProps(arg)) {
      throw new TypeError('h: props must be the first argument');
    }
    out.push(arg);
  }
  return out;
}

const factories = new Map<string, Factory>();

function factoryFor(tag: string): Factory {
  const cached = factories.get(tag);
  if (cached) return cached;
  const factory: Factory = (...args) => element(tag, ...args);
  factories.set(tag, factory);
  return factory;
}

const target: H = Object.freeze(Object.create(null));

// SAFETY: every string key resolves to the same tag factory shape; the proxy
// target is empty and `get` is the only trap, so H's per-tag call signatures
// describe exactly what a property access returns.
export const h = new Proxy(target, {
  get(_target, tag) {
    if (!isString(tag)) return undefined;
    return factoryFor(tag);
  },
});

